import { useCallback, useMemo, useRef } from "react";
import { WORD_ID_ATTRIBUTE, resolveElementId } from "../lib/viewport";

let transactionCounter = 0;
const nextTransactionId = (): string => `gesture-${(transactionCounter += 1)}`;

/**
 * How far apart a stroke is hit-tested along the path between two reported
 * positions, in CSS pixels.
 *
 * The browser reports a moving finger once a frame, and a quick stroke covers
 * tens of pixels in that time: hit-testing only where each report lands skipped
 * a third of the words a scripted stroke crossed, the short ones and those
 * across a line gap most of all. Close enough that the narrowest word at the
 * smallest font size cannot fall between two samples.
 */
export const SAMPLE_SPACING = 4;

/**
 * The most hit-tests one move may make. A finger flung across the page in a
 * single frame is sampled more coarsely rather than stalling that frame.
 */
export const MAX_SAMPLES = 64;

export interface GestureHandlers {
  onPointerDown: (event: React.PointerEvent) => void;
  onPointerMove: (event: React.PointerEvent) => void;
  onPointerUp: (event: React.PointerEvent) => void;
  onPointerCancel: (event: React.PointerEvent) => void;
}

export interface GestureCallbacks {
  /**
   * Called with the elements a stroke has newly crossed, at most once per
   * pointer event, in the order the stroke reached them.
   *
   * `paint` is the direction the stroke committed to on the first element it
   * reached: a stroke that begins on an unmarked word marks everything it
   * crosses, and vice versa, so dragging back and forth does not flicker.
   * `transaction` groups the whole stroke into a single undo step.
   */
  onPaint: (ids: string[], paint: boolean, transaction: string) => void;
  /** Reads the current marked state of an element, to decide stroke direction. */
  isMarked: (id: string) => boolean | undefined;
  onGestureEnd?: () => void;
}

interface Point {
  x: number;
  y: number;
}

/**
 * The positions a move reports. A browser that coalesces a frame's moves into
 * one event keeps the ones it folded in, and the path runs through all of them.
 */
const positionsOf = (event: React.PointerEvent): Point[] => {
  const coalesced = event.nativeEvent.getCoalescedEvents?.() ?? [];
  const source = coalesced.length > 0 ? coalesced : [event];
  return source.map(({ clientX, clientY }) => ({ x: clientX, y: clientY }));
};

/**
 * Tracks a drag across marked elements.
 *
 * This hook deliberately owns no copy of the page: it resolves ids from the DOM
 * and reports them, leaving the reducer as the single source of truth. An earlier
 * version kept a parallel immer copy of the whole page and synced it back and
 * forth with effects, which is what made undo/redo unreliable.
 *
 * A stroke begins wherever the pointer goes down on the surface — a gutter, the
 * sliver between two lines — and takes its direction from the first word it
 * reaches. Beginning only on a word made every stroke that started a little
 * wide of the text erase nothing at all, however many words it then crossed;
 * the app this grew out of never had that dead zone. (Starting off a word was
 * once refused because the surface held the toolbar's buttons and an empty
 * stroke wrote history. Neither is true any more.)
 *
 * The text column declares `touch-action: none`, so the browser never takes a
 * stroke on the text away mid-drag, in whatever direction it travels. The
 * gutters stay `pan-y`: a finger set down there and moved vertically is the
 * browser's to scroll, which it announces with `pointercancel`, and moved
 * across into the text it erases. A previous version also drove a two-finger
 * scroll from here, which cost a scroll mode, a map of live touches and a way
 * to take back the word the opening finger had already erased; the gutter
 * turned out to be enough.
 */
export const useGesture = (
  { onPaint, isMarked, onGestureEnd }: GestureCallbacks,
  attribute: string = WORD_ID_ATTRIBUTE,
) => {
  // Refs, not state, all of them: they change many times per stroke and must be
  // read synchronously by the very next pointermove. `isActive` was state, which
  // meant a render of the whole editor at each end of every stroke for a value
  // nothing outside this hook ever read — and a `move` arriving in the same task
  // as its `pointerdown` would have read the stale one.
  const pointerRef = useRef<number | undefined>(undefined);
  const paintRef = useRef<boolean | undefined>(undefined);
  const transactionRef = useRef<string | undefined>(undefined);
  const visitedRef = useRef(new Set<string>());
  const lastRef = useRef<Point | undefined>(undefined);

  const paint = useCallback(
    (ids: Iterable<string | undefined>) => {
      const fresh: string[] = [];
      for (const id of ids) {
        if (!id || visitedRef.current.has(id)) continue;
        const current = isMarked(id);
        if (current === undefined) continue;
        visitedRef.current.add(id);
        if (paintRef.current === undefined) paintRef.current = !current;
        fresh.push(id);
      }
      if (fresh.length === 0 || paintRef.current === undefined) return;
      transactionRef.current ??= nextTransactionId();
      onPaint(fresh, paintRef.current, transactionRef.current);
    },
    [isMarked, onPaint],
  );

  /**
   * The ids under the path from the last position to each new one.
   *
   * `elementFromPoint` reads layout, which the previous mark has dirtied — bounded
   * to one paragraph by the containment on `Paragraph`, without which it cost
   * fourteen milliseconds a move on a long article. Only the first sample of a
   * move pays that; nothing is written between them, so the rest are hit-tests
   * against a layout that is already clean.
   */
  const trace = useCallback(
    (positions: Point[]): string[] => {
      const ids: string[] = [];
      let from = lastRef.current ?? positions[0];
      for (const to of positions) {
        const distance = Math.hypot(to.x - from.x, to.y - from.y);
        const steps = Math.min(
          MAX_SAMPLES,
          Math.max(1, Math.ceil(distance / SAMPLE_SPACING)),
        );
        for (let step = 1; step <= steps; step += 1) {
          const x = from.x + ((to.x - from.x) * step) / steps;
          const y = from.y + ((to.y - from.y) * step) / steps;
          const id = resolveElementId(document.elementFromPoint(x, y), attribute);
          if (id && ids[ids.length - 1] !== id) ids.push(id);
        }
        from = to;
      }
      lastRef.current = from;
      return ids;
    },
    [attribute],
  );

  const clearStroke = useCallback(() => {
    visitedRef.current.clear();
    paintRef.current = undefined;
    transactionRef.current = undefined;
    lastRef.current = undefined;
    pointerRef.current = undefined;
  }, []);

  const start = useCallback(
    (event: React.PointerEvent) => {
      // A right- or middle-click is not a stroke. A second finger landing
      // mid-stroke takes the stroke over, and the first one's moves are ignored
      // from then on: ignoring the newcomer instead would leave every later
      // touch refused if a stroke's ending were ever lost.
      if (event.pointerType === "mouse" && event.button !== 0) return;

      // Keep receiving moves even if the pointer leaves the element it started on.
      (event.currentTarget as Element).setPointerCapture?.(event.pointerId);
      clearStroke();
      pointerRef.current = event.pointerId;
      lastRef.current = { x: event.clientX, y: event.clientY };
      // The target, not a hit-test: at `pointerdown` it is still the element
      // under the pointer, and reading it costs no layout.
      paint([resolveElementId(event.target as Element, attribute)]);
    },
    [attribute, clearStroke, paint],
  );

  const move = useCallback(
    (event: React.PointerEvent) => {
      if (pointerRef.current !== event.pointerId) return;
      // With pointer capture the event target is pinned to the capturing
      // element, so the path has to be hit-tested by coordinate.
      paint(trace(positionsOf(event)));
    },
    [paint, trace],
  );

  const stop = useCallback(
    (event: React.PointerEvent) => {
      if (pointerRef.current !== event.pointerId) return;
      (event.currentTarget as Element).releasePointerCapture?.(event.pointerId);
      clearStroke();
      onGestureEnd?.();
    },
    [clearStroke, onGestureEnd],
  );

  const handlers: GestureHandlers = useMemo(
    () => ({
      onPointerDown: start,
      onPointerMove: move,
      onPointerUp: stop,
      onPointerCancel: stop,
    }),
    [start, move, stop],
  );

  return { handlers };
};
